import { useEffect } from "react";
import { roleLabel, sortedRoles } from "../features/admin-users/roles";
import type { SessionUser } from "./session";

/**
 * Who a Loupe entry was captured under (root operator, 2026-10-14).
 *
 * Loupe frames this application, and its SDK records page, element, view and
 * build with every entry - but not who was signed in here. A finding about
 * the rights matrix has no verdict without that: a missing *Status aendern*
 * is right for a Manager and a defect for an Incident Owner. Half the matrix
 * is "own incidents only", a relation between the account and the incident
 * that a reader can recover from the timeline only if the account is named.
 * One tester works through several accounts under one Loupe identity, so
 * Loupe cannot know which; only this application can say it.
 *
 * The SDK reads two attributes on its own script tag when an entry is
 * captured (Loupe v0.44.0), beside `data-loupe-build`:
 *
 *   data-loupe-role  the roles, German labels in canonical order, " + "
 *   data-loupe-user  the display name - what every other account may see of
 *                    this one ([F-030]); never the mail address, because every
 *                    member of the Loupe project reads every published entry
 *
 * Both say what the user menu says, so the entry names what the tester saw.
 * They follow `/me`: a role an administrator changes mid-session shows after
 * the next re-read, as it does in the menu. Without a session both are gone.
 *
 * A third, `data-loupe-context`, is a JSON object of short lines Loupe shows
 * beside them. The incident page writes the incident's number and how the
 * account is involved in it, so "own incidents only" is stated on the entry
 * rather than recovered from the timeline; leaving the page removes it.
 */

export interface LoupeContext {
  role: string;
  user: string;
}

/** The SDK's tag - the build plugin sets `data-loupe-build` on it in every mode (vite.config.ts). */
export const LOUPE_SCRIPT_SELECTOR = "script[data-loupe-build]";

/** The two values for a signed-in person, or null without a session. */
export function loupeContext(user: SessionUser | null, t: (key: string) => string): LoupeContext | null {
  if (!user) return null;
  if (user.isSystem === true) return { role: t("system.role"), user: t("system.name") };
  const role =
    user.roles.length > 0 ? sortedRoles(user.roles).map((r) => roleLabel(t, r)).join(" + ") : t("auth.noRoles");
  return { role, user: user.displayName };
}

/** The part of a DOM element this needs - narrow, so the rule is testable without a DOM. */
interface AttributeTarget {
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
}

/**
 * Writes the context onto the SDK's tag, or removes it. A build without Loupe
 * has no such tag, and this does nothing.
 */
export function applyLoupeContext(
  root: { querySelector(selector: string): AttributeTarget | null },
  context: LoupeContext | null,
): void {
  writeLoupeAttribute(root, "data-loupe-role", context?.role ?? null);
  writeLoupeAttribute(root, "data-loupe-user", context?.user ?? null);
}

/** One attribute on the SDK's tag: set, or removed for null. */
function writeLoupeAttribute(
  root: { querySelector(selector: string): AttributeTarget | null },
  name: string,
  value: string | null,
): void {
  const tag = root.querySelector(LOUPE_SCRIPT_SELECTOR);
  if (!tag) return;
  if (value === null) tag.removeAttribute(name);
  else tag.setAttribute(name, value);
}

const INVOLVEMENT_KEY = {
  owner: "loupe.involvements.owner",
  assignee: "loupe.involvements.assignee",
  ownerAndAssignee: "loupe.involvements.ownerAndAssignee",
  none: "loupe.involvements.none",
} as const;

/**
 * The context lines for an incident page: its number, and whether the
 * signed-in account is its Owner, an assigned Bearbeiter, both or neither -
 * the relation half the rights matrix turns on ("eigene" Vorfaelle).
 */
export function incidentContext(
  incident: { number: string; ownerId: string | null; assigneeIds: readonly string[] },
  userId: string,
  t: (key: string) => string,
): Record<string, string> {
  const owner = incident.ownerId === userId;
  const assignee = incident.assigneeIds.includes(userId);
  const involvement = owner && assignee ? "ownerAndAssignee" : owner ? "owner" : assignee ? "assignee" : "none";
  return { [t("loupe.incident")]: incident.number, [t("loupe.involvement")]: t(INVOLVEMENT_KEY[involvement]) };
}

/** Holds `data-loupe-context` while the calling page shows these lines; null or unmounting removes it. */
export function useLoupeContextLines(lines: Record<string, string> | null): void {
  const value = lines ? JSON.stringify(lines) : null;
  useEffect(() => {
    writeLoupeAttribute(document, "data-loupe-context", value);
    return () => writeLoupeAttribute(document, "data-loupe-context", null);
  }, [value]);
}
